import { Component } from '@theme/component';

/**
 * Editorial chapter index — a fixed marginal rail that lists the page's
 * sections and tracks which one is in view.
 *
 * It builds itself from the DOM rather than from settings: every DRAPP
 * section already prints an eyebrow label ("02 — Der Vergleich"), so the
 * rail stays correct when the merchant adds, removes or reorders sections
 * in the theme editor without anyone maintaining a second list.
 *
 * Deliberately desktop-only and hidden below three chapters — on a short
 * page or a narrow screen it would be chrome without a job.
 *
 * @extends {Component}
 */
export class DrappChapterIndex extends Component {
  /**
   * Must stay identical to the guard in drapp-craft.css §6b. Below this width
   * the product column runs to within 40px of the viewport edge and the rail
   * would be drawn straight over the bundle picker.
   */
  static BREAKPOINT = '(min-width: 1500px)';

  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {Map<Element, HTMLButtonElement>} */
  #links = new Map();
  /** @type {MediaQueryList | null} */
  #media = null;
  /** @type {(() => void) | null} */
  #onMediaChange = null;

  connectedCallback() {
    super.connectedCallback();

    // Scheduled twice on purpose. requestAnimationFrame is the right moment —
    // it waits a frame so sections rendered later in the same paint are
    // counted — but a browser never runs animation frames in a background
    // tab. A page opened with a middle-click or restored from a previous
    // session would therefore build its index never, not merely late, because
    // nothing retried once the tab was brought forward. The timeout is the
    // floor that guarantees a build regardless of visibility; #build starts by
    // tearing down, so whichever of the two lands first, the result is
    // identical and running both is harmless.
    requestAnimationFrame(() => this.#build());
    setTimeout(() => this.#build(), 0);

    // The rail is shown only where the layout has a gutter for it (see
    // drapp-craft.css §6b). Rebuilding on that same breakpoint keeps the
    // JavaScript and the stylesheet from disagreeing about when it exists —
    // previously the index was built once at load and never reconsidered, so
    // resizing across the breakpoint left it in the wrong state.
    this.#media = window.matchMedia(DrappChapterIndex.BREAKPOINT);
    this.#onMediaChange = () => this.#build();
    this.#media.addEventListener('change', this.#onMediaChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#media && this.#onMediaChange) {
      this.#media.removeEventListener('change', this.#onMediaChange);
    }
    this.#media = null;
    this.#onMediaChange = null;
    this.#teardown();
  }

  #build() {
    this.#teardown();

    if (!window.matchMedia(DrappChapterIndex.BREAKPOINT).matches) return;

    const main = document.querySelector('#MainContent') ?? document;
    const chapters = [];

    for (const section of main.querySelectorAll('drapp-reveal-component')) {
      const label = section.querySelector('.drp-label');
      const raw = label?.textContent?.trim();
      if (!raw) continue;

      // "02 — Der Vergleich" → number and title shown separately. Sections
      // without an explicit number show none: inventing one here collided
      // with the real numbering (two chapters both reading "01").
      const parts = raw.match(/^(\d+)\s*[—–-]\s*(.+)$/);
      chapters.push({
        section,
        number: parts ? parts[1] : '',
        title: parts ? parts[2] : raw,
      });
    }

    if (chapters.length < 3) return;

    const nav = document.createElement('nav');
    nav.className = 'drp-chapters__nav';
    nav.setAttribute('aria-label', 'Abschnitte dieser Seite');

    for (const chapter of chapters) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'drp-chapters__item';
      button.innerHTML =
        '<span class="drp-chapters__num"></span><span class="drp-chapters__title"></span>';
      // textContent, never innerHTML, for merchant-authored strings.
      button.querySelector('.drp-chapters__num').textContent = chapter.number;
      button.querySelector('.drp-chapters__title').textContent = chapter.title;
      button.addEventListener('click', () => {
        // Reveal the destination before travelling to it. Jumping lands the
        // viewport on a section whose entrance animation has not run yet, so
        // without this the reader arrives at a blank band and waits out an
        // 0.8s fade — the one moment where the effect works against them.
        chapter.section.dataset.reveal = 'shown';
        chapter.section.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
      });
      nav.appendChild(button);
      this.#links.set(chapter.section, button);
    }

    this.replaceChildren(nav);
    this.dataset.ready = 'true';

    // Tall sections can both straddle the sampling band, which marked two
    // chapters current at once. Track what is visible and highlight only the
    // topmost of them, so exactly one entry is ever active.
    const visible = new Set();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        let current = null;
        for (const section of this.#links.keys()) {
          if (visible.has(section)) { current = section; break; }
        }
        for (const [section, button] of this.#links) {
          button.classList.toggle('is-current', section === current);
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );

    for (const section of this.#links.keys()) this.#observer.observe(section);
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#links.clear();
    this.replaceChildren();
    delete this.dataset.ready;
  }
}

if (!customElements.get('drapp-chapter-index-component')) {
  customElements.define('drapp-chapter-index-component', DrappChapterIndex);
}
